import { AssetType } from "../../types/AssetType";
import { blackScholesModelCalculationService } from "../BlackScholesModelCalculationService";
import { PortfolioGroupedItem } from "../../../screens/positions-and-orders/models/PortfolioGroupedItem";
import { portfolioCalculationService } from "../portfolio-calculation-service/PortfolioCalculationService";

export class PortfolioPositionsBalanceCalculationService {
    calculateAtCurrent(
        portfolioPositions: PortfolioGroupedItem[],
        underlyingPrice: number,
        getImpliedVolatility: (position: PortfolioGroupedItem) => number
    ): number {
        let balance = 0;

        portfolioPositions.map(position => {
            balance += position.currentSize === 0
                ? position.volume
                : this.calculate(position, underlyingPrice, new Date(), getImpliedVolatility);
        });

        return balance;
    }

    calculateAtCustom(
        portfolioPositions: PortfolioGroupedItem[],
        underlyingPrice: number,
        customDate: Date,
        getImpliedVolatility: (position: PortfolioGroupedItem) => number,
    ): number {
        let balance = 0;

        portfolioPositions.map(position => {
            balance += position.currentSize === 0
                ? position.volume
                : this.calculate(position, underlyingPrice, customDate, getImpliedVolatility);
        });

        return balance;
    }
    
    calculateAtExpiration(portfolioPositions: PortfolioGroupedItem[], underlyingPrice: number): number {
        let balance = 0;

        portfolioPositions.map(position => {
            balance += position.currentSize === 0
                ? position.volume
                : this.calculateBalanceAtExpiration(position, underlyingPrice);
        });

        return balance;
    }
    
    calculate(
        position: PortfolioGroupedItem,
        underlyingPrice: number,
        fromDate: Date,
        getImpliedVolatility: (position: PortfolioGroupedItem) => number,
    ): number {
        if (position.type === AssetType.Spot ||
            position.type === AssetType.Futures
        ){
            return position.volume + position.currentSize * underlyingPrice;
        }
        else if (position.type === AssetType.Call || position.type === AssetType.Put
        ) {
            if (position.expireAt === null)
                throw new Error("Options expiration date is null")
            
            const isExpired = blackScholesModelCalculationService
                .getTimeToExpirationInShareOfYear(position.expireAt) < 0;
            
            const timeToExpirationInShareOfYear = blackScholesModelCalculationService
                .getTimeToExpirationInShareOfYear(position.expireAt, fromDate);
            
            if (isExpired || timeToExpirationInShareOfYear < 0) {
                return portfolioCalculationService.balance.calculateGroupedPositionBalanceAfterExpiration(position)
            } else {
                const optionMarkPrice = this.calculateOptionMarkPrice(position,
                    underlyingPrice, timeToExpirationInShareOfYear, getImpliedVolatility);
                
                return position.volume + position.currentSize * optionMarkPrice;
            }
        }

        throw new Error("Unknown AssetType");
    }
    
    calculateOptionMarkPrice(
        position: PortfolioGroupedItem,
        underlyingPrice: number,
        timeToExpirationInShareOfYear: number,
        getImpliedVolatility: (position: PortfolioGroupedItem) => number,
    ): number {
        if (position.impliedVolatility === null) {
            throw new Error("ImpliedVolatility cannot be null!");
        }

        if (position.customImpliedVolatility === null) {
            throw new Error("CustomImpliedVolatility cannot be null!");
        }

        if (position.strike === null) {
            throw new Error("Portfolio item property 'strike' is null.")
        }

        return blackScholesModelCalculationService.getPremium(
            position.type, underlyingPrice, position.strike,
            timeToExpirationInShareOfYear, getImpliedVolatility(position));
    }

    // todo: why it isn't used?
    calculateBalanceAfterExpiration(position: PortfolioGroupedItem): number {
        if (position.type !== AssetType.Call && position.type !== AssetType.Put)
            throw new Error("Position must be only option")

        if (position.expireAt === null)
            throw new Error("Options expiry date is null")

        if (position.expirationPrice === null)
            throw new Error("Options expiry price is null")

        if (position.strike === null)
            throw new Error("Options strike is null")

        return position.type === AssetType.Call
            ? position.strike < position.expirationPrice
                ? position.volume + (position.expirationPrice - position.strike) * position.currentSize
                : position.volume
            : position.strike > position.expirationPrice
                ? position.volume + (position.strike - position.expirationPrice) * position.currentSize
                : position.volume;
    }
    
    private calculateBalanceAtExpiration(
        position: PortfolioGroupedItem,
        underlyingPrice: number
    ): number {
        if (position.type === AssetType.Call) {
            if (position.strike === null) {
                throw new Error("Portfolio item property 'strike' is null.")
            }

            if (position.expireAt === null)
                throw new Error("Options expiration date is null.")

            const isExpired = blackScholesModelCalculationService
                .getTimeToExpirationInShareOfYear(position.expireAt) < 0;

            if (!isExpired) {
                return position.strike < underlyingPrice
                    ? position.volume + (underlyingPrice - position.strike) * position.currentSize
                    : position.volume;
            } else {
                return portfolioCalculationService.balance.calculateGroupedPositionBalanceAfterExpiration(position);
            }
        }
        else if (position.type === AssetType.Put) {
            if (position.strike === null) {
                throw new Error("Portfolio item property 'strike' is null.")
            }

            if (position.expireAt === null)
                throw new Error("Options expiration date is null.")

            const isExpired = blackScholesModelCalculationService
                .getTimeToExpirationInShareOfYear(position.expireAt) < 0;

            return !isExpired
                ? position.strike > underlyingPrice
                    ? position.volume + (position.strike - underlyingPrice) * position.currentSize
                    : position.volume
                : portfolioCalculationService.balance.calculateGroupedPositionBalanceAfterExpiration(position);
        }
        else if (
            position.type === AssetType.Futures ||
            position.type === AssetType.Spot
        ) {
            return position.volume + position.currentSize * underlyingPrice;
        }

        throw new Error("Unknown AssetType");
    }
}